import { useState, useCallback, useRef, useEffect } from 'react';
import useCustomers from '../../hooks/useCustomers';
import { updateOrderStatus, updateOrderNotes } from '../../services/orders';
import CustomerForm from '../../components/employee/CustomerForm';
import Table from '../../components/common/Table';
import Pagination from '../../components/common/Pagination';
import Alert from '../../components/common/Alert';
import Loading from '../../components/common/Loading';
import Modal from '../../components/common/Modal';
import { formatDate, formatPhone } from '../../utils/formatters';
import './EmployeeListPage.css';
import './Customers.css';

const PAGE_SIZE = 20;

// Production CRM base URL for deep-linking a linked wholesale account.
const CRM_URL = import.meta.env.VITE_CRM_URL || 'https://crm.deeprootsmilling.com';

const EmployeeCustomers = () => {
  // ── View state ───────────────────────────────────────────
  const [view, setView] = useState('list'); // 'list' | 'detail'
  const [selectedCustomer, setSelectedCustomer] = useState(null);
  const [detailOrders, setDetailOrders] = useState([]);
  const [detailUsers, setDetailUsers] = useState([]);
  const [detailArchivedUsers, setDetailArchivedUsers] = useState([]);
  const [archivedUsersLoaded, setArchivedUsersLoaded] = useState(false);
  const [detailLoading, setDetailLoading] = useState(false);

  // ── List filters ─────────────────────────────────────────
  const [searchTerm, setSearchTerm] = useState('');
  const debounceRef = useRef(null);

  // ── Alerts ───────────────────────────────────────────────
  const [alert, setAlert] = useState(null);

  // ── Data hook ────────────────────────────────────────────
  const {
    customers,
    loading,
    isRefreshing,
    isSaving,
    error,
    pagination,
    fetchCustomers,
    fetchCustomer,
    fetchOrders,
    fetchUsers,
    archiveUser,
    unarchiveUser,
    addAddress,
    editAddress,
    removeAddress,
    makeDefaultAddress,
    createCustomer,
    updateCustomer,
    addNote,
    editNote,
    removeNote,
    clearError,
  } = useCustomers();

  // ── New customer modal ───────────────────────────────────
  const [showNewCustomerModal, setShowNewCustomerModal] = useState(false);
  const [newCustomerForm, setNewCustomerForm] = useState({
    company_name: '', phone: '', delivery_region: '',
    first_name: '', last_name: '', email: '',
  });
  const [newCustomerError, setNewCustomerError] = useState(null);

  const handleNewCustomerChange = useCallback((field, value) => {
    setNewCustomerForm((prev) => ({ ...prev, [field]: value }));
  }, []);

  const handleNewCustomerClose = useCallback(() => {
    setShowNewCustomerModal(false);
    setNewCustomerForm({ company_name: '', phone: '', delivery_region: '', first_name: '', last_name: '', email: '' });
    setNewCustomerError(null);
  }, []);

  const handleNewCustomerSubmit = useCallback(async (e) => {
    e.preventDefault();
    setNewCustomerError(null);
    try {
      await createCustomer(newCustomerForm);
      handleNewCustomerClose();
      fetchCustomers({ search: '', page: 1 });
      setAlert({ type: 'success', message: 'Customer created successfully.' });
    } catch (err) {
      const detail = err?.company_name?.[0] || err?.email?.[0] || err?.message || 'Failed to create customer.';
      setNewCustomerError(detail);
    }
  }, [createCustomer, newCustomerForm, handleNewCustomerClose, fetchCustomers]);

  // ── Build current params ──────────────────────────────────
  const buildParams = useCallback(
    (overrides = {}) => ({
      search: searchTerm,
      page: pagination.page,
      ...overrides,
    }),
    [searchTerm, pagination.page]
  );

  // ── Debounced search ─────────────────────────────────────
  const handleSearchChange = (e) => {
    const val = e.target.value;
    setSearchTerm(val);
    clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      fetchCustomers({ search: val, page: 1 });
    }, 300);
  };

  // ── Pagination ───────────────────────────────────────────
  const handlePageChange = (page) => {
    fetchCustomers(buildParams({ page }));
  };

  // ── Row click → detail ───────────────────────────────────
  const handleRowClick = async (row) => {
    try {
      setDetailLoading(true);
      setAlert(null);

      const [company, ordersData, usersData] = await Promise.all([
        fetchCustomer(row.id),
        fetchOrders(row.id),
        fetchUsers(row.id),
      ]);

      setSelectedCustomer(company);
      setDetailOrders(ordersData?.results || ordersData?.orders || []);
      setDetailUsers(usersData?.results || usersData?.users || []);
      setDetailArchivedUsers([]);
      setArchivedUsersLoaded(false);
      setView('detail');
    } catch {
      setAlert({ type: 'error', message: 'Failed to load customer details.' });
    } finally {
      setDetailLoading(false);
    }
  };

  // ── Back to list ──────────────────────────────────────────
  const handleCancel = () => {
    setSelectedCustomer(null);
    setDetailOrders([]);
    setDetailUsers([]);
    setDetailArchivedUsers([]);
    setArchivedUsersLoaded(false);
    setView('list');
  };

  // ── Address helpers — refresh selectedCustomer after mutation ──
  const refreshSelectedCustomer = useCallback(async () => {
    if (!selectedCustomer) return;
    try {
      const updated = await fetchCustomer(selectedCustomer.id);
      setSelectedCustomer(updated);
    } catch {
      // non-fatal; stale data is better than crashing
    }
  }, [selectedCustomer, fetchCustomer]);

  const handleAddAddress = useCallback(async (addressData) => {
    await addAddress(addressData);
    await refreshSelectedCustomer();
  }, [addAddress, refreshSelectedCustomer]);

  const handleEditAddress = useCallback(async (addressId, addressData) => {
    await editAddress(addressId, addressData);
    await refreshSelectedCustomer();
  }, [editAddress, refreshSelectedCustomer]);

  const handleDeleteAddress = useCallback(async (addressId) => {
    await removeAddress(addressId);
    await refreshSelectedCustomer();
  }, [removeAddress, refreshSelectedCustomer]);

  const handleSetDefaultAddress = useCallback(async (addressId) => {
    await makeDefaultAddress(addressId);
    await refreshSelectedCustomer();
  }, [makeDefaultAddress, refreshSelectedCustomer]);

  // ── Customer update helper ──────────────────────────────
  const handleUpdateCustomer = useCallback(async (companyId, data) => {
    const updated = await updateCustomer(companyId, data);
    setSelectedCustomer(updated);
    return updated;
  }, [updateCustomer]);

  // ── Note helpers ──────────────────────────────────────────
  const handleAddNote = useCallback(async (companyId, body) => {
    await addNote(companyId, body);
    await refreshSelectedCustomer();
  }, [addNote, refreshSelectedCustomer]);

  const handleEditNote = useCallback(async (noteId, body) => {
    await editNote(noteId, body);
    await refreshSelectedCustomer();
  }, [editNote, refreshSelectedCustomer]);

  const handleDeleteNote = useCallback(async (noteId) => {
    await removeNote(noteId);
    await refreshSelectedCustomer();
  }, [removeNote, refreshSelectedCustomer]);

  // ── Contact archive helpers ─────────────────────────────────
  const refreshActiveUsers = useCallback(async () => {
    if (!selectedCustomer) return;
    const data = await fetchUsers(selectedCustomer.id);
    setDetailUsers(data?.results || data?.users || []);
  }, [selectedCustomer, fetchUsers]);

  const handleLoadArchivedUsers = useCallback(async () => {
    if (!selectedCustomer) return;
    const data = await fetchUsers(selectedCustomer.id, { includeArchived: true });
    const all = data?.results || data?.users || [];
    setDetailArchivedUsers(all.filter((u) => u.archived_at));
    setArchivedUsersLoaded(true);
  }, [selectedCustomer, fetchUsers]);

  const handleArchiveUser = useCallback(async (profileId, reason) => {
    await archiveUser(profileId, reason);
    await refreshActiveUsers();
    if (archivedUsersLoaded) await handleLoadArchivedUsers();
  }, [archiveUser, refreshActiveUsers, archivedUsersLoaded, handleLoadArchivedUsers]);

  const handleUnarchiveUser = useCallback(async (profileId) => {
    await unarchiveUser(profileId);
    await refreshActiveUsers();
    await handleLoadArchivedUsers();
  }, [unarchiveUser, refreshActiveUsers, handleLoadArchivedUsers]);

  // ── Order mutations ───────────────────────────────────────
  const handleOrderStatusUpdate = useCallback(async (orderId, newStatus) => {
    // Let the error propagate to CustomerForm, which handles optimistic update + revert.
    // Only sync detailOrders after the API confirms success.
    const updated = await updateOrderStatus(orderId, newStatus);
    setDetailOrders((prev) =>
      prev.map((o) => (o.id === orderId ? { ...o, status: updated.status ?? newStatus } : o))
    );
    return updated;
  }, []);

  const handleOrderNotesUpdate = useCallback(async (orderId, notes) => {
    await updateOrderNotes(orderId, notes);
  }, []);

  // ── Cleanup ───────────────────────────────────────────────
  useEffect(() => {
    return () => clearTimeout(debounceRef.current);
  }, []);

  // ── Sync hook errors into alert ───────────────────────────
  useEffect(() => {
    if (error) {
      setAlert({ type: 'error', message: error.message || 'An error occurred.' });
      clearError();
    }
  }, [error, clearError]);

  // ── Table columns ─────────────────────────────────────────
  const columns = [
    { key: 'company_name', label: 'Company' },
    {
      key: 'phone',
      label: 'Phone',
      render: (val) => val ? formatPhone(val) : <span style={{ color: 'var(--emp-color-muted, #b8b0a4)' }}>—</span>,
    },
    {
      key: 'order_count',
      label: 'Orders',
      render: (val) => val ?? 0,
    },
    {
      key: 'user_count',
      label: 'Contacts',
      render: (val) => val ?? 0,
    },
    {
      key: 'created_at',
      label: 'Customer Since',
      render: (val) => formatDate(val),
    },
  ];

  // ── Render ────────────────────────────────────────────────

  return (
    <div className="employee-page customers-page">
      {/* ── Alert ──────────────────────────────────────── */}
      {alert && (
        <Alert
          type={alert.type}
          message={alert.message}
          dismissible
          onDismiss={() => setAlert(null)}
          className="emp-list__alert"
        />
      )}

      {view === 'list' ? (
        <>
          {/* ── Header ──────────────────────────────────── */}
          <div className="employee-page__header">
            <p className="employee-page__eyebrow">Employee Portal</p>
            <h1 className="employee-page__title">Customers</h1>
          </div>

          {/* ── Toolbar ─────────────────────────────────── */}
          <div className="emp-list__toolbar">
            <div className="emp-list__toolbar-left">
              <div className="emp-list__search-wrap">
                <svg className="emp-list__search-icon" viewBox="0 0 20 20" aria-hidden="true">
                  <path d="M8.5 3a5.5 5.5 0 014.383 8.823l3.896 3.9a.75.75 0 01-1.06 1.06l-3.9-3.896A5.5 5.5 0 118.5 3zm0 1.5a4 4 0 100 8 4 4 0 000-8z" fill="currentColor" />
                </svg>
                <input
                  type="text"
                  className="emp-list__search"
                  placeholder="Search companies..."
                  value={searchTerm}
                  onChange={handleSearchChange}
                  aria-label="Search customers"
                />
              </div>
              <button
                type="button"
                className="customers-new-btn"
                onClick={() => setShowNewCustomerModal(true)}
              >
                + New Customer
              </button>
            </div>
            {pagination.count > 0 && (
              <span className="customers-count__label">
                {pagination.count} {pagination.count === 1 ? 'company' : 'companies'}
              </span>
            )}
          </div>

          {/* ── Content body ────────────────────────────── */}
          <div className="emp-list__body">
            {loading || detailLoading ? (
              <Loading text={detailLoading ? 'Loading customer...' : 'Loading customers...'} />
            ) : (
              <>
                {/* Desktop table */}
                <Table
                  columns={columns}
                  data={customers}
                  onRowClick={handleRowClick}
                  emptyMessage="No customers found."
                  className="emp-list__table"
                />

                {/* Mobile cards */}
                <div className="emp-list__cards">
                  {customers.length === 0 ? (
                    <p className="emp-list__cards-empty">No customers found.</p>
                  ) : (
                    customers.map((company) => (
                      <button
                        key={company.id}
                        type="button"
                        className="emp-list__card"
                        onClick={() => handleRowClick(company)}
                      >
                        <div className="emp-list__card-top">
                          <span className="emp-list__card-name">{company.company_name}</span>
                        </div>
                        {company.phone && (
                          <div className="emp-list__card-subtitle">{formatPhone(company.phone)}</div>
                        )}
                        <div className="customers-card__meta">
                          <span className="customers-card__meta-item">
                            {company.order_count ?? 0} orders
                          </span>
                          <span className="customers-card__meta-item">
                            {company.user_count ?? 0} contacts
                          </span>
                        </div>
                        <div className="emp-list__card-date">Since {formatDate(company.created_at)}</div>
                        <svg className="emp-list__card-chevron" viewBox="0 0 16 16" aria-hidden="true">
                          <path d="M6 4L10 8L6 12" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                        </svg>
                      </button>
                    ))
                  )}
                </div>

                <Pagination
                  currentPage={pagination.page}
                  totalCount={pagination.count}
                  pageSize={PAGE_SIZE}
                  onPageChange={handlePageChange}
                />
              </>
            )}
            {isRefreshing && (
              <div className="emp-list__refreshing" />
            )}
          </div>
        </>
      ) : (
        <>
          {/* ── Detail header ────────────────────────────── */}
          <div className="employee-page__header">
            <p className="employee-page__eyebrow">Employee Portal / Customers</p>
            <div className="emp-customer-detail__titlebar">
              <h1 className="employee-page__title">{selectedCustomer?.company_name}</h1>
              {selectedCustomer?.crm_contact_id && (
                <a
                  className="emp-customer-detail__crm-link"
                  href={`${CRM_URL}/contacts/${selectedCustomer.crm_contact_id}`}
                  target="_blank"
                  rel="noreferrer"
                >
                  View in CRM ↗
                </a>
              )}
            </div>
          </div>

          {/* ── Detail body ──────────────────────────────── */}
          <div className="emp-list__body emp-list__body--form">
            {detailLoading ? (
              <Loading text="Loading customer..." />
            ) : (
              <CustomerForm
                customer={selectedCustomer}
                orders={detailOrders}
                users={detailUsers}
                archivedUsers={detailArchivedUsers}
                archivedUsersLoaded={archivedUsersLoaded}
                onCancel={handleCancel}
                onUpdateCustomer={handleUpdateCustomer}
                onAddAddress={handleAddAddress}
                onEditAddress={handleEditAddress}
                onDeleteAddress={handleDeleteAddress}
                onSetDefaultAddress={handleSetDefaultAddress}
                onAddNote={handleAddNote}
                onEditNote={handleEditNote}
                onDeleteNote={handleDeleteNote}
                onOrderStatusUpdate={handleOrderStatusUpdate}
                onOrderNotesUpdate={handleOrderNotesUpdate}
                onArchiveUser={handleArchiveUser}
                onUnarchiveUser={handleUnarchiveUser}
                onLoadArchivedUsers={handleLoadArchivedUsers}
                isSaving={isSaving}
              />
            )}
          </div>
        </>
      )}
      {/* ── New Customer Modal ──────────────────────────── */}
      <Modal
        isOpen={showNewCustomerModal}
        onClose={handleNewCustomerClose}
        title="New Customer"
        size="medium"
      >
        <form className="new-customer-form" onSubmit={handleNewCustomerSubmit}>
          {newCustomerError && (
            <p className="new-customer-form__error">{newCustomerError}</p>
          )}

          <p className="new-customer-form__section-label">Company</p>
          <div className="new-customer-form__fields">
            <div className="new-customer-form__field new-customer-form__field--full">
              <label className="new-customer-form__label" htmlFor="nc-company-name">
                Company Name <span className="new-customer-form__required">*</span>
              </label>
              <input
                id="nc-company-name"
                type="text"
                className="new-customer-form__input"
                value={newCustomerForm.company_name}
                onChange={(e) => handleNewCustomerChange('company_name', e.target.value)}
                required
                disabled={isSaving}
              />
            </div>
            <div className="new-customer-form__field">
              <label className="new-customer-form__label" htmlFor="nc-phone">Phone</label>
              <input
                id="nc-phone"
                type="tel"
                className="new-customer-form__input"
                value={newCustomerForm.phone}
                onChange={(e) => handleNewCustomerChange('phone', e.target.value)}
                placeholder="e.g. 540-555-1234"
                disabled={isSaving}
              />
            </div>
            <div className="new-customer-form__field">
              <label className="new-customer-form__label" htmlFor="nc-region">Delivery Region</label>
              <select
                id="nc-region"
                className="new-customer-form__input"
                value={newCustomerForm.delivery_region}
                onChange={(e) => handleNewCustomerChange('delivery_region', e.target.value)}
                disabled={isSaving}
              >
                <option value="">— none —</option>
                <option value="local">local</option>
                <option value="cville">cville</option>
                <option value="east">east</option>
                <option value="north">north</option>
                <option value="west">west</option>
                <option value="south">south</option>
                <option value="far">far</option>
                <option value="valley">valley</option>
              </select>
            </div>
          </div>

          <p className="new-customer-form__section-label">Contact (optional)</p>
          <div className="new-customer-form__fields">
            <div className="new-customer-form__field">
              <label className="new-customer-form__label" htmlFor="nc-first-name">First Name</label>
              <input
                id="nc-first-name"
                type="text"
                className="new-customer-form__input"
                value={newCustomerForm.first_name}
                onChange={(e) => handleNewCustomerChange('first_name', e.target.value)}
                disabled={isSaving}
              />
            </div>
            <div className="new-customer-form__field">
              <label className="new-customer-form__label" htmlFor="nc-last-name">Last Name</label>
              <input
                id="nc-last-name"
                type="text"
                className="new-customer-form__input"
                value={newCustomerForm.last_name}
                onChange={(e) => handleNewCustomerChange('last_name', e.target.value)}
                disabled={isSaving}
              />
            </div>
            <div className="new-customer-form__field new-customer-form__field--full">
              <label className="new-customer-form__label" htmlFor="nc-email">Email</label>
              <input
                id="nc-email"
                type="email"
                className="new-customer-form__input"
                value={newCustomerForm.email}
                onChange={(e) => handleNewCustomerChange('email', e.target.value)}
                disabled={isSaving}
              />
            </div>
          </div>

          <div className="new-customer-form__actions">
            <button
              type="button"
              className="new-customer-form__cancel-btn"
              onClick={handleNewCustomerClose}
              disabled={isSaving}
            >
              Cancel
            </button>
            <button
              type="submit"
              className="new-customer-form__submit-btn"
              disabled={isSaving || !newCustomerForm.company_name.trim()}
            >
              {isSaving ? 'Creating…' : 'Create Customer'}
            </button>
          </div>
        </form>
      </Modal>
    </div>
  );
};

export default EmployeeCustomers;
